import React, { useState, useEffect } from 'react'
import { ArrowPathIcon, CogIcon, DocumentArrowDownIcon, UserIcon, EnvelopeIcon, PhoneIcon, MapPinIcon, TrashIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { useJobDescriptions } from '../contexts/JobDescriptionContext'
import { useResumes } from '../contexts/ResumeContext'
import { useCredits } from '../contexts/CreditContext'
import { useToast } from '../contexts/ToastContext'
import { apiClient } from '../utils/api'
import { ResumeStatus, Resume, SortOption, StatusOption, ApiResponse } from '../types'

export function ResumeTable() {
  const { showSuccess, showError } = useToast();
  const [selectedJdId, setSelectedJdId] = useState<string>('')
  const [selectedStatuses, setSelectedStatuses] = useState<ResumeStatus[]>([])
  const [processing, setProcessing] = useState(false)
  const [processingProgress, setProcessingProgress] = useState<{ current: number; total: number; message: string } | null>(null)
  const [editingStatus, setEditingStatus] = useState<string | null>(null)
  const [expandedReasoning, setExpandedReasoning] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [showReprocessModal, setShowReprocessModal] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null)
  const [showProcessNewModal, setShowProcessNewModal] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  
  const { jobDescriptions } = useJobDescriptions()
  const { resumes, loading, fetchResumes } = useResumes()
  const { credits, refreshCredits } = useCredits()

  const statusOptions: StatusOption[] = [
    { value: 'new', label: 'New', color: 'badge-warning' },
    { value: 'processing', label: 'Processing', color: 'badge-info' },
    { value: 'processed', label: 'Processed', color: 'badge-primary' },
    { value: 'shortlisted', label: 'Shortlisted', color: 'badge-accent' },
    { value: 'interview', label: 'Interview', color: 'badge-accent' },
    { value: 'offered', label: 'Offered', color: 'badge-success' },
    { value: 'rejected', label: 'Rejected', color: 'badge-error' },
    { value: 'declined', label: 'Declined', color: 'badge-ghost' },
    { value: 'joined', label: 'Joined', color: 'badge-success' },
  ]

  useEffect(() => {
    fetchResumes()
  }, [fetchResumes])

  // Add status to filter
  const addStatusFilter = (status: ResumeStatus) => {
    if (!selectedStatuses.includes(status)) {
      setSelectedStatuses(prev => [...prev, status])
    }
  }

  // Remove status from filter
  const removeStatusFilter = (status: ResumeStatus) => {
    setSelectedStatuses(prev => prev.filter(s => s !== status))
  }

  // Clear all status filters
  const clearStatusFilters = () => {
    setSelectedStatuses([])
  }

  // Sort resumes based on selected option
  const sortedResumes = [...resumes].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    } else {
      // highest first - sort by fitment score descending
      return (b.fitment_score || -1) - (a.fitment_score || -1)
    }
  })

  // Filter resumes by selected job description and statuses
  const filteredResumes = sortedResumes.filter(resume => {
    const matchesJd = selectedJdId ? resume.jd_id === selectedJdId : true
    const matchesStatus = selectedStatuses.length > 0 ? selectedStatuses.includes(resume.status) : true
    return matchesJd && matchesStatus
  })

  // Check credits before processing
  const checkCreditsForProcessing = async (resumeCount: number, operation: string) => {
    const currentBalance = credits?.credit_balance || 0;
    
    if (currentBalance < resumeCount) {
      showError(`Insufficient credits! You need ${resumeCount} credits but have ${currentBalance}. Please purchase more credits to continue.`);
      return false;
    }

    if (currentBalance <= 5 && currentBalance >= resumeCount) {
      const proceed = window.confirm(
        `⚠️ Low Credits Warning!\n\nYou have ${currentBalance} credits remaining. This ${operation} will use ${resumeCount} credit(s), leaving you with ${currentBalance - resumeCount} credits.\n\nDo you want to continue?`
      );
      if (!proceed) return false;
    }

    return true;
  };

  const handleProcessNewClick = () => {
    const newResumes = filteredResumes.filter(r => r.status === 'new');
    if (newResumes.length === 0) {
      showError('No new resumes to process');
      return;
    }
    setShowProcessNewModal(true);
  };

  const processNewResumes = async () => {
    const newResumes = filteredResumes.filter(r => r.status === 'new');
    if (newResumes.length === 0) {
      showError('No new resumes to process');
      return;
    }

    const hasEnoughCredits = await checkCreditsForProcessing(newResumes.length, 'processing');
    if (!hasEnoughCredits) {
      return;
    }

    setProcessing(true);
    setProcessingProgress({ current: 0, total: newResumes.length, message: 'Starting batch processing...' });
    
    try {
      // Use batch processing for better UX and to avoid timeouts
      const result = await apiClient.post<ApiResponse<Resume[]>>('/resumes/process/batches', {
        batchSize: 20
      });
      
      if (result.success) {
        setProcessingProgress({ current: newResumes.length, total: newResumes.length, message: 'Processing completed!' });
        
        // Show success message after a brief delay to show completion
        setTimeout(() => {
          showSuccess(`Successfully processed ${newResumes.length} resume(s) (${newResumes.length} credits used)`);
          setProcessingProgress(null);
        }, 1000);
        
        await fetchResumes();
        await refreshCredits(); // Refresh credit balance
      } else {
        showError(result.message || 'Failed to process resumes');
        setProcessingProgress(null);
      }
    } catch (error) {
      console.error('Error processing resumes:', error);
      setProcessingProgress(null);
      
      if (error instanceof Error && error.message.includes('402')) {
        showError('Insufficient credits. Please purchase more credits to continue.');
      } else if (error instanceof Error && error.message.includes('422')) {
        showError('Some resumes failed due to AI processing errors. Credits have been refunded for failed items. Please try reprocessing the failed resumes.');
      } else if (error instanceof Error && error.message.includes('timeout')) {
        showError('Processing timed out. The system is processing resumes in the background. Please refresh the page in a few minutes to see updated results.');
      } else {
        showError(error instanceof Error ? error.message : 'Failed to process resumes');
      }
    } finally {
      setProcessing(false);
    }
  };

  const updateResumeStatus = async (resumeId: string, newStatus: ResumeStatus) => {
    try {
      await apiClient.put(`/resumes/${resumeId}/status`, { status: newStatus })
      showSuccess('Resume status updated successfully')
      await fetchResumes()
      setEditingStatus(null)
    } catch (error) {
      console.error('Error updating status:', error)
      showError(error instanceof Error ? error.message : 'Failed to update resume status')
    }
  }

  const reprocessResume = async (resumeId: string) => {
    // Check credits first
    const hasEnoughCredits = await checkCreditsForProcessing(1, 'reprocessing');
    if (!hasEnoughCredits) {
      return;
    }

    try {
      await apiClient.post(`/resumes/${resumeId}/reprocess`)
      showSuccess('Resume reprocessed successfully (1 credit used)')
      await fetchResumes()
      await refreshCredits() // Refresh credit balance
    } catch (error) {
      console.error('Error reprocessing:', error)
      if (error instanceof Error && error.message.includes('402')) {
        showError('Insufficient credits. Please purchase more credits to continue.')
      } else if (error instanceof Error && error.message.includes('422')) {
        showError('AI processing failed due to parsing error. Credit has been refunded. Please try reprocessing.')
      } else {
        showError(error instanceof Error ? error.message : 'Failed to reprocess resume')
      }
    }
  }

  const deleteResume = async (resumeId: string) => {
    setDeleting(resumeId)
    try {
      await apiClient.delete(`/resumes/${resumeId}`)
      showSuccess('Resume deleted successfully')
      await fetchResumes()
    } catch (error) {
      console.error('Error deleting resume:', error)
      showError(error instanceof Error ? error.message : 'Failed to delete resume')
    } finally {
      setDeleting(null)
      setShowDeleteModal(null)
    }
  }

  const toggleReasoning = (resumeId: string) => {
    const newExpanded = new Set(expandedReasoning)
    if (newExpanded.has(resumeId)) {
      newExpanded.delete(resumeId)
    } else {
      newExpanded.add(resumeId)
    }
    setExpandedReasoning(newExpanded)
  }

  const getStatusBadgeColor = (status: ResumeStatus) => {
    return statusOptions.find(opt => opt.value === status)?.color || 'badge-ghost'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="card bg-base-100 shadow-sm border border-base-300">
        <div className="card-body">
          <div className="flex items-center justify-center py-8">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h2 className="card-title text-xl underline">Resume Analysis</h2>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <select 
              className="select select-bordered select-sm"
              value={selectedJdId}
              onChange={(e) => setSelectedJdId(e.target.value)}
              aria-label="Select job description for processing"
            >
              <option value="">Select Job Description</option>
              {jobDescriptions.map(jd => (
                <option key={jd.id} value={jd.id}>
                  {jd.title}
                </option>
              ))}
            </select>

            <select
              className="select select-bordered select-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              aria-label="Sort resumes"
            >
              <option value="newest">Newest first</option>
              <option value="highest">Highest first</option>
            </select>

            {/* Status Filter Dropdown */}
            <div className="dropdown dropdown-end">
              <div tabIndex={0} role="button" className="btn btn-outline btn-sm w-32">
                <FunnelIcon className="h-4 w-4 mr-1" />
                Status Filter 
                {selectedStatuses.length > 0 && (
                  <span className="badge badge-primary badge-xs ml-1">
                    {selectedStatuses.length}
                  </span>
                )}
              </div>
              <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
                {statusOptions.map(option => (
                  <li key={option.value}>
                    <label className="label cursor-pointer">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={selectedStatuses.includes(option.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addStatusFilter(option.value)
                          } else {
                            removeStatusFilter(option.value)
                          }
                        }}
                      />
                      <span className="label-text">{option.label}</span>
                    </label>
                  </li>
                ))}
                {selectedStatuses.length > 0 && (
                  <li className="menu-title">
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={clearStatusFilters}
                    >
                      Clear All
                    </button>
                  </li>
                )}
              </ul>
            </div>

            <button 
              className={`btn btn-primary btn-sm ${processing ? 'loading' : ''}`}
              onClick={handleProcessNewClick}
              disabled={processing || resumes.filter(r => r.status === 'new').length === 0}
              aria-label="Process new resumes"
            >
              {processing ? (
                <div className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-sm"></span>
                  <span>Processing...</span>
                </div>
              ) : (
                <>
                  <CogIcon className="h-4 w-4 mr-1" />
                  Process New ({resumes.filter(r => r.status === 'new').length})
                </>
              )}
            </button>
          </div>
        </div>

        {/* Processing Progress */}
        {processingProgress && (
          <div className="mb-4">
            <div className="alert alert-info">
              <div className="flex items-center gap-3">
                <span className="loading loading-spinner loading-sm"></span>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{processingProgress.message}</span>
                    <span className="text-sm text-base-content/70">
                      {processingProgress.current} / {processingProgress.total}
                    </span>
                  </div>
                  <progress 
                    className="progress progress-primary w-full" 
                    value={processingProgress.current} 
                    max={processingProgress.total}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selected Status Filters as Chiclets */}
        {selectedStatuses.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-base-content/70">Filtering by:</span>
              {selectedStatuses.map(status => {
                const statusOption = statusOptions.find(opt => opt.value === status)
                return (
                  <div
                    key={status}
                    className={`badge gap-1 cursor-pointer hover:brightness-90 ${statusOption?.color || 'badge-ghost'}`}
                    onClick={() => removeStatusFilter(status)}
                  >
                    {statusOption?.label || status}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )
              })}
              <button
                className="btn btn-ghost btn-xs"
                onClick={clearStatusFilters}
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Credit Warning */}
        {credits && credits.credit_balance <= 5 && (
          <div className={`alert ${credits.credit_balance === 0 ? 'alert-error' : 'alert-warning'} mb-4`}>
            <span>
              {credits.credit_balance === 0 
                ? '🚨 No credits remaining! You need to purchase credits to process resumes.'
                : `⚠️ Low credits warning! You have ${credits.credit_balance} credits remaining.`
              }
            </span>
          </div>
        )}

        {resumes.length === 0 ? (
          <div className="text-center py-8 text-base-content/60">
            No resumes uploaded yet. Upload some resumes to get started.
          </div>
        ) : filteredResumes.length === 0 ? (
          <div className="text-center py-8 text-base-content/60">
            {selectedJdId 
              ? "No resumes found for the selected job description."
              : "No resumes uploaded yet. Upload some resumes to get started."
            }
          </div>
        ) : (
          <div className="space-y-4">
            {filteredResumes.map((resume) => {
              const jobDescription = jobDescriptions.find(jd => jd.id === resume.jd_id)
              const isExpanded = expandedReasoning.has(resume.id)
              
              return (
                <div key={resume.id} className="card bg-base-100 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="card-body">
                    {/* Header with Status and Score */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <DocumentArrowDownIcon className="h-6 w-6 text-primary flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <a 
                              href={resume.file_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="font-semibold text-lg hover:text-primary transition-colors cursor-pointer block truncate"
                              style={{ textDecoration: 'none' }}
                            >
                              {resume.file_name}
                            </a>
                            
                            {/* Status and Score in first row */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {editingStatus === resume.id ? (
                                <select
                                  className="select select-xs select-bordered"
                                  defaultValue={resume.status}
                                  onChange={(e) => updateResumeStatus(resume.id, e.target.value as ResumeStatus)}
                                  onBlur={() => setEditingStatus(null)}
                                  autoFocus
                                  aria-label="Update resume status"
                                >
                                  {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div 
                                  className={`badge badge-sm cursor-pointer ${getStatusBadgeColor(resume.status)}`}
                                  onClick={() => setEditingStatus(resume.id)}
                                >
                                  {statusOptions.find(opt => opt.value === resume.status)?.label || resume.status}
                                  {resume.status === 'processing' && (
                                    <span className="loading loading-spinner loading-xs ml-1"></span>
                                  )}
                                </div>
                              )}
                              
                              {resume.fitment_score >= 0 && (
                                <div className={`badge badge-sm ${
                                  resume.fitment_score >= 80 ? 'badge-success' :
                                  resume.fitment_score >= 60 ? 'badge-warning' :
                                  'badge-error'
                                }`}>
                                  Fitment Score: {resume.fitment_score}%
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-base-content/60 truncate">{jobDescription?.title || 'Unknown JD'}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          className="btn btn-ghost btn-md"
                          onClick={() => setShowReprocessModal(resume.id)}
                          title="Reprocess (1 credit)"
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                        </button>
                        <button
                          className="btn btn-ghost btn-md text-error hover:text-error"
                          onClick={() => setShowDeleteModal(resume.id)}
                          title="Delete resume"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {/* Candidate Info */}
                    {resume.name && (
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-base">
                          <UserIcon className="h-4 w-4 text-base-content/60" />
                          <span className="font-medium">{resume.name}</span>
                        </div>
                        {resume.email && (
                          <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <EnvelopeIcon className="h-3 w-3" />
                            <span>{resume.email}</span>
                          </div>
                        )}
                        {resume.phone && (
                          <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <PhoneIcon className="h-3 w-3" />
                            <span>{resume.phone}</span>
                          </div>
                        )}
                        {resume.location && (
                          <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MapPinIcon className="h-3 w-3" />
                            <span>{resume.location}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Experience and Skills in same row */}
                    {(resume.experience || resume.key_skills) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        {resume.experience && (
                          <div className="p-3 bg-base-200 rounded-lg">
                            <div className="text-sm font-medium text-base-content/70 mb-1">Experience:</div>
                            <div className="text-sm text-base-content/80">{resume.experience}</div>
                          </div>
                        )}
                        {resume.key_skills && (
                          <div className="p-3 bg-base-200 rounded-lg">
                            <div className="text-sm font-medium text-base-content/70 mb-1">Key Skills:</div>
                            <div className="text-sm text-base-content/80">{resume.key_skills}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Analysis Details */}
                    {resume.fitment_justification && resume.fitment_justification !== 'not evaluated yet' && (
                      <div className="mb-4">
                        <div className="p-3 bg-base-200 rounded-lg">
                          <div className="text-sm text-base-content/80 whitespace-pre-wrap">
                            {isExpanded 
                              ? resume.fitment_justification
                              : resume.fitment_justification.split('\n').slice(0, 2).join('\n') + 
                                (resume.fitment_justification.split('\n').length > 2 ? '...' : '')
                            }
                          </div>
                          {resume.fitment_justification.split('\n').length > 2 && (
                            <button
                              className="btn btn-ghost btn-xs mt-2"
                              onClick={() => toggleReasoning(resume.id)}
                            >
                              {isExpanded ? 'See Less' : 'See More'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm text-base-content/60">
                      <span>Uploaded {formatDate(resume.created_at)}</span>
                      <span>ID: {resume.id.slice(0, 8)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Process New Confirmation Modal */}
        {showProcessNewModal && (
          <div className="modal modal-open">
            <div className="modal-box">
              <h3 className="font-bold text-lg mb-4">Confirm Process New Resumes</h3>
              <p className="mb-4">
                This will process all new resumes using AI analysis. This action will cost <strong>{filteredResumes.filter(r => r.status === 'new').length} credits</strong>.
              </p>
              <p className="mb-6 text-sm text-base-content/70">
                Current balance: <strong>{credits?.credit_balance || 0} credits</strong><br/>
                Resumes to process: <strong>{filteredResumes.filter(r => r.status === 'new').length} resumes</strong><br/>
                Remaining balance after processing: <strong>{Math.max(0, (credits?.credit_balance || 0) - filteredResumes.filter(r => r.status === 'new').length)} credits</strong>
              </p>
              <div className="modal-action">
                <button 
                  className="btn btn-ghost"
                  onClick={() => setShowProcessNewModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    processNewResumes()
                    setShowProcessNewModal(false)
                  }}
                >
                  Process New ({filteredResumes.filter(r => r.status === 'new').length} credits)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reprocess Confirmation Modal */}
        {showReprocessModal && (
          <div className="modal modal-open">
            <div className="modal-box">
              <h3 className="font-bold text-lg mb-4">Confirm Reprocess</h3>
              <p className="mb-4">
                This will reprocess the resume using AI analysis. This action will cost <strong>1 credit</strong>.
              </p>
              <p className="mb-6 text-sm text-base-content/70">
                Current balance: <strong>{credits?.credit_balance || 0} credits</strong>
              </p>
              <div className="modal-action">
                <button 
                  className="btn btn-ghost"
                  onClick={() => setShowReprocessModal(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    reprocessResume(showReprocessModal)
                    setShowReprocessModal(null)
                  }}
                >
                  Reprocess (1 credit)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal modal-open">
            <div className="modal-box">
              <h3 className="font-bold text-lg mb-4">Confirm Delete</h3>
              <p className="mb-4">
                Are you sure you want to delete this resume? This action cannot be undone.
              </p>
              <p className="mb-6 text-sm text-base-content/70">
                The resume file and all associated data will be permanently removed.
              </p>
              <div className="modal-action">
                <button 
                  className="btn btn-ghost"
                  onClick={() => setShowDeleteModal(null)}
                >
                  Cancel
                </button>
                <button 
                  className={`btn btn-error ${deleting === showDeleteModal ? 'loading' : ''}`}
                  onClick={() => deleteResume(showDeleteModal)}
                  disabled={deleting === showDeleteModal}
                >
                  {deleting === showDeleteModal ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Resume'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 